import { useEffect, useState, useCallback } from "react";
import usePersistedFilters from "../../hooks/usePersistedFilters";
import { useLocation, useNavigate } from "react-router-dom";
import { MdEast, MdWest, MdOutlineHistory } from "react-icons/md";
import { useTranslation } from "react-i18next";

import PageHeader from "../../shared/PageHeader";
import useFetchData from "../../hooks/UseFetchData";
import CustomPagination from "../../shared/CustomPagination";
import CustomTable from "../../shared/CustomTable";
import { formatDateTime } from '../../utils/FormateDateTime';
import { FaArrowLeft } from 'react-icons/fa';
import CheckPermission from '../../hooks/CheckPermission';

const InvoiceAuditPage = () => {
  const { hasPermission } = CheckPermission();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const location = useLocation();
  const navigate = useNavigate();
  const invoice_number = location.state?.invoice_number;

  const { data, loading, executeFetch } = useFetchData();

  const { filters: payloadData, onPageChange: onPayloadPageChange } = usePersistedFilters(
    'invoice_audit',
    { page: 1, page_size: 20 }
  );
  const [expandedRows, setExpandedRows] = useState({});

  const canView = hasPermission('billing', 'get') || hasPermission('management', 'get');

  const fetchAuditData = useCallback(async () => {
    if (!canView) return;
    const body = {
      page: payloadData.page,
      page_size: payloadData.page_size,
    };
    if (invoice_number) body.invoice_number = invoice_number;
    await executeFetch("POST", "/v3/invoices/audit", body);
  }, [payloadData, invoice_number, executeFetch, canView]);

  useEffect(() => {
    if (!invoice_number) return;
    fetchAuditData();
  }, [invoice_number, fetchAuditData]);

  const onPageChange = (newPage, newRowsPerPage) => {
    onPayloadPageChange(newPage, newRowsPerPage);
  };

  const formatSimpleValue = (val) => {
    if (val === null || val === undefined || val === "") return "---";
    if (typeof val === 'boolean') return val ? t("Yes") : t("No");
    if (typeof val === 'number') return String(val);
    if (typeof val === 'string') return val;
    return JSON.stringify(val);
  };

  const friendlyLabel = (str) => {
    if (!str) return '---';
    return str.replace(/_/g, ' ').replace(/\./g, ' › ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const renderValue = (val) => {
    if (val === null || val === undefined || val === "") return <span className="text-gray-300 italic">{t("empty")}</span>;
    if (typeof val === 'boolean') return <span className="font-semibold">{val ? t("Yes") : t("No")}</span>;
    if (typeof val === 'number') return <span className="font-mono">{val.toLocaleString()}</span>;
    if (typeof val === 'string') {
      if (val.startsWith('<') && val.endsWith('>')) {
        const text = val.replace(/<[^>]*>/g, '').trim();
        return <span>{text || <span className="text-gray-300 italic">{t("empty")}</span>}</span>;
      }
      return <span>{val}</span>;
    }
    if (Array.isArray(val)) {
      if (val.length === 0) return <span className="text-gray-300 italic">{t("empty")}</span>;
      if (val.every(item => typeof item !== 'object')) return <span>{val.join(', ')}</span>;
      return <span className="font-mono text-[10px] break-all">{JSON.stringify(val)}</span>;
    }
    if (typeof val === 'object') {
      return (
        <div className="flex flex-col gap-0.5 text-[10px]">
          {Object.entries(val).map(([k, v], i) => (
            <span key={i}>{friendlyLabel(k)}: <strong>{formatSimpleValue(v)}</strong></span>
          ))}
        </div>
      );
    }
    return <span>{String(val)}</span>;
  };


  const renderDiffRow = (label, oldVal, newVal) => (
    <div className="flex items-stretch border-b border-gray-100 last:border-b-0">
      <div className={`w-[22%] shrink-0 px-2 py-1.5 bg-gray-50/80 flex flex-col justify-center border-gray-100 ${isRTL ? 'border-l' : 'border-r'}`}>
        <span className="text-[10px] font-medium text-gray-700">{friendlyLabel(label)}</span>
      </div>
      <div className={`flex-1 flex items-stretch min-w-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex-1 px-2 py-1.5 bg-red-50/40 border-gray-100 flex items-center min-w-0 ${isRTL ? 'border-l' : 'border-r'}`}>
          <div className="text-[10px] text-red-700/70 break-words min-w-0 w-full">{renderValue(oldVal)}</div>
        </div>
        <div className="flex items-center px-1 bg-gray-50/50">
          {isRTL ? <MdWest size={10} className="text-gray-300 shrink-0" /> : <MdEast size={10} className="text-gray-300 shrink-0" />}
        </div>
        <div className="flex-1 px-2 py-1.5 bg-green-50/40 flex items-center min-w-0">
          <div className="text-[10px] text-green-800 font-medium break-words min-w-0 w-full">{renderValue(newVal)}</div>
        </div>
      </div>
    </div>
  );


  const getChangeType = (change) => {
    if (change.field_name?.startsWith('item_added')) return 'added';
    if (change.field_name?.startsWith('item_removed')) return 'removed';
    return 'modified';
  };


  const rowExpansionTemplate = (audit) => {
    const changes = audit.changes || [];

    if (changes.length === 0) {
      return (
        <div className="px-2 py-1.5" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="border border-gray-100 rounded bg-gray-50/50 p-4 text-center">
            <span className="text-[10px] text-gray-400 italic">
              {t("No tracked field changes recorded for this update.")}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="px-2 py-1.5 space-y-1.5" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex items-center gap-4 px-2 py-1 bg-gray-50 rounded border border-gray-100 text-[10px] text-gray-500">
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-200"></span>
            <span>{t("Old Value")}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded bg-green-100 border border-green-200"></span>
            <span>{t("New Value")}</span>
          </div>
        </div>

        <div className="border border-gray-200 rounded overflow-hidden">
          <div className="px-2 py-1 bg-gray-100/80 border-b border-gray-200 flex items-center gap-1.5">
            <span className="text-[10px] font-semibold text-gray-700">{t("Changed Fields")}</span>
            <span className="text-[8px] text-gray-400 bg-gray-200/60 px-1 py-0.5 rounded">{changes.length}</span>
          </div>
          {changes.map((change, cIdx) => {
            const type = getChangeType(change);
            return (
              <div key={cIdx} className={type === 'added' ? 'border-l-2 border-green-400' : type === 'removed' ? 'border-l-2 border-red-400' : ''}>
                {renderDiffRow(change.field_name, change.old_value, change.new_value)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const columns = [
    {
      header: "",
      style: { width: "3.5rem" },
      extraProps: { expander: true }
    },
    {
      header: t("Invoice Number"),
      body: (audit) => (
        audit?.cash_invoice_number || "---"
      )
    },
    {
      header: t("Updated At"),
      body: (audit) => (
        audit.timestamp ? formatDateTime(audit.timestamp) : "---"
      )
    },
    {
      header: t("Updated By"),
      body: (audit) => (
        audit.performed_by?.full_name || "---"
      )
    },
    {
      header: t("Role"),
      body: (audit) => (
        audit.performed_by?.role
      )
    },
    {
      header: t("Changes"),
      body: (audit) => (
        <span>
          {audit.changes?.length ?? 0} {t("field(s)")}
        </span>
      )
    },
    {
      header: t("Reason"),
      body: (audit) => (
        audit.change_reason
          ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-100">
            {audit.change_reason}
          </span>
          : <span className="text-[11px] text-gray-400">---</span>
      ),
      bodyCellClass: "!whitespace-normal"
    }
  ];

  const totalCount = data?.total_count ?? data?.totalCount ?? 0;
  const auditList = data?.audits ?? data?.data ?? [];

  return (
    <div className="w-full h-full bg-white !overflow-y-auto scrollbar p-4">
      <PageHeader title={t("Invoice Update History")} Icon={FaArrowLeft} onIconClick={() => navigate(-1)} />
      <div className="w-full mt-4">
        {loading ? (
          <div className="space-y-3">
            <div className="h-10 bg-gray-50 rounded animate-pulse"></div>
            {[1, 2, 3, 4, 5]?.map(i => (
              <div key={i} className="h-14 bg-gray-50 border border-gray-100 rounded animate-pulse"></div>
            ))}
          </div>
        ) : auditList?.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 border border-gray-100 rounded-lg">
            <MdOutlineHistory size={48} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-500 font-medium">{t("No invoice audit records found")}</p>
          </div>
        ) : (
          <div className="w-full space-y-6">
            <CustomTable
              data={auditList}
              columns={columns}
              isLoading={loading}
              dataKey="id"
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              rowClassName={() => "cursor-pointer"}
              onRowClick={(e) => {
                if (e.originalEvent.target.closest('.p-row-toggler')) return;
                const id = e.data.id;
                if (!id) return;
                setExpandedRows((prev) => {
                  if (prev && prev[id]) return {};
                  return { [id]: true };
                });
              }}
            />

            {totalCount > payloadData?.page_size && (
              <div className="pt-4 flex justify-center pb-6 border-t border-gray-100">
                <CustomPagination
                  currentPage={payloadData?.page}
                  rowsPerPage={payloadData?.page_size}
                  totalRecords={totalCount}
                  totalPages={Math.ceil(totalCount / payloadData?.page_size)}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceAuditPage;
